import StripePaymentElement from '@/app/components/payment/components/method/components/stripe-payment-element';
import StripeExpressCheckoutElement from '@/app/components/payment/components/method/components/stripe-express-checkout-element';
import ZipCodeInputV2 from '@/app/components/payment/components/method/components/zip-code-input-v2';
import CouponCodeInput from '@/app/components/payment/components/method/components/coupon-code-input';
import { PriceDataType } from '@/app/components/payment/components/method/payment-method-v2.controller';
import { Flex, Divider, useBreakpointValue } from '@chakra-ui/react';
import { Box, Text } from '@cmpsr/components';
import { StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js';
import { UseFormReturn } from 'react-hook-form';
import { PaymentFormState } from '../PPRAPaymentPage';
import SecurePayments from './SecurePayments';
import { useState } from 'react';

interface PaymentSectionProps {
  setIsPaymentComplete: (value: boolean) => void;
  showPaymentError: boolean;
  setShowPaymentError: (value: boolean) => void;
  form: UseFormReturn<PaymentFormState>;
  getPrice: () => void;
  paymentSectionRef: React.RefObject<HTMLDivElement>;
  handleSubmit?: () => void;
  handleExpressCheckout?: (event: StripeExpressCheckoutElementConfirmEvent) => Promise<void>;
  priceData?: PriceDataType;
  priceRecurring?: {
    interval?: string;
    intervalCount?: number;
  };
  coupon: string;
  setCoupon: (coupon: string) => void;
  couponValidationError: string | false | null;
  isSubmitting: boolean;
  isTrial?: boolean;
}

const PaymentSection = ({
  setIsPaymentComplete,
  showPaymentError,
  setShowPaymentError,
  paymentSectionRef,
  form,
  getPrice,
  handleSubmit,
  handleExpressCheckout,
  priceData,
  priceRecurring,
  coupon,
  setCoupon,
  couponValidationError,
  isSubmitting,
  isTrial = false,
}: PaymentSectionProps) => {
  const [isExpressCheckoutReady, setIsExpressCheckoutReady] = useState(false);
  // Show express checkout for trials ($0) or regular payments (> $0)
  const shouldShowExpressCheckout = isTrial || !!(priceData?.summary?.amount && priceData.summary.amount > 0);
  const useWideLayout = useBreakpointValue({ base: false, lg: true });

  return (
    <Box
      ref={paymentSectionRef}
      width="100%"
      backgroundColor="transparent"
      display="flex"
      flexDirection="column"
      gap="25px"
    >
      <Box p="12px" display="flex" flexDirection="column" gap="8px">
        {shouldShowExpressCheckout ? (
          <Box
            sx={{
              '& #express-checkout-element': {
                '& > div': {
                  gap: '8px !important',
                },
                '& button': {
                  borderRadius: '100px !important',
                  height: '50px !important',
                }
              }
            }}
          >
          {priceData && (
            <StripeExpressCheckoutElement
              key={`express-checkout-${priceData.summary.amount}`}
              priceAmount={priceData.summary.amount}
              recurring={priceRecurring}
              useWideLayout={useWideLayout}
              isTrial={isTrial}
              onConfirm={async (event) => {
              if (handleExpressCheckout) {
                await handleExpressCheckout(event);
              } else {
                console.error('Express Checkout handler not provided');
                event.paymentFailed({
                  reason: 'fail'
                });
              }
            }}
            onSuccess={() => {
              setIsPaymentComplete(true);
              setShowPaymentError(false);
            }}
            onReady={() => {
              setIsExpressCheckoutReady(true);
            }}
          />
          )}
        </Box>
        ) : null}

        {shouldShowExpressCheckout && isExpressCheckoutReady && (
          <Box width="100%" display="flex" alignItems="center" my={3}>
            <Box height="1px" bg="#e6e6e6" flex="1" />
            <Text
              px={2}
              fontSize="15px"
              color="#77787d"
              fontFamily="Inter"
              fontWeight="normal"
            >
              or pay with card
            </Text>
            <Box height="1px" bg="#e6e6e6" flex="1" />
          </Box>
        )}

        <StripePaymentElement
          onChange={(event) => {
            setIsPaymentComplete(event.complete);
            if (event.complete) setShowPaymentError(false);
          }}
        />
        {showPaymentError && (
          <Text color="salmon-light" fontSize="xs" mt="-15px" pb="15px" zIndex="2">
            Please check your payment information and try again.
          </Text>
        )}

        <ZipCodeInputV2 form={form} getPrice={getPrice} />

        <CouponCodeInput
          form={form}
          getPrice={getPrice}
          coupon={coupon}
          setCoupon={setCoupon}
          couponValidationError={couponValidationError}
          isSubmitting={isSubmitting}
        />
      </Box>
    </Box>
  );
};

export default PaymentSection;
